import FileInput, { IFileTokenProps } from "@components/inputs/fileInput/FileInput";
import { ISharedInputProps } from "@components/inputs/input";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext from "@odata/BindingContext";
import { IEntityAttachmentEntity } from "@odata/GeneratedEntityTypes";
import FileStorage from "@utils/FileStorage";
import { isObjectEmpty } from "@utils/general";
import saveAs from "file-saver";
import React from "react";

import { IValidationError } from "../../../model/Validator.types";
import { FormStorage } from "../../../views/formView/FormStorage";

interface IProps extends ISharedInputProps {
    storage: FormStorage;
    bindingContext: BindingContext;
    onChange: (e: ISmartFieldChange) => void;
    width?: string;
    error?: IValidationError;
    multiple?: boolean;
    useFullPageDropArea?: boolean;
    value?: TSmartFileInputValue;
    dontUpload?: boolean;
    isDisabled?: boolean;
    disableFileOpening?: boolean;
}

type TSmartFileInputValue = IEntityAttachmentEntity[] | IEntityAttachmentEntity;

class SmartFileInput extends React.Component<IProps> {
    fileInputRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        this.props.storage.addOtherDropArea(this.fileInputRef?.current);
    }

    componentWillUnmount() {
        this.props.storage.removeOtherDropArea(this.fileInputRef?.current);
    }

    get files(): IEntityAttachmentEntity[] {
        const value = this.props.storage.getValue(this.props.bindingContext);
        return !isObjectEmpty(value) ? (Array.isArray(value) ? value : [value]) : [];
    }

    handleChange = (value: IEntityAttachmentEntity[]): void => {
        const { storage, bindingContext, multiple, onChange } = this.props;

        if (storage.hasError(bindingContext)) {
            storage.clearError(bindingContext);
        }

        if (!multiple && Array.isArray(value)) {
            onChange({
                value: value[0],
                bindingContext
            });
        } else {
            onChange({
                value: value.map(val => val.Id),
                bindingContext,
                selectedItems: value?.map(attachment => ({
                    id: attachment.Id,
                    additionalData: attachment
                })),
                triggerAdditionalTasks: true
            });
        }
    };

    handleNewFiles = async (files: File[]): Promise<void> => {
        let resultFiles: IEntityAttachmentEntity[];
        if (this.props.dontUpload) {
            resultFiles = files.map((file) => ({
                File: {
                    Id: undefined,
                    Name: file.name,
                    File: file,
                } as IEntityAttachmentEntity,
            }));
        } else {
            const fileMetadataPromises = files.map((file) => {
                return FileStorage.upload({ file, name: file.name });
            });
            const filesMetadata = await Promise.all(fileMetadataPromises);
            const newFiles = filesMetadata.map((file) => ({
                File: file,
                Id: file.Id
            }));
            resultFiles = [...(this.files), ...newFiles];
        }
        this.handleChange(resultFiles);
        this.props.storage.refresh(true); // todo: move somewhere else, it's just refresh correctly color of file strip
    };

    handleRemove = (id: string, e: React.MouseEvent): void => {
        if (this.props.isReadOnly || this.props.isDisabled) {
            return;
        }
        if (this.props.storage.data.selectedFile?.Id?.toString() === id) {
            this.props.storage.selectFile(null);
        }

        if (!id) {
            this.handleChange([]);
        } else {
            const tokens = this.files.filter((file) => file.File.Id?.toString() !== id);
            this.handleChange(tokens);
        }
        e.preventDefault();
        e.stopPropagation();
    };

    handleClick = (id: string): void => {
        const attachmentEntity = this.files.find(file => file.File?.Id?.toString() === id);
        const downloadInstead = this.props.isReadOnly && !this.props.storage.data.definition.files.enabledInReadOnlyMode;
        if (downloadInstead && attachmentEntity?.File) {
            const { Id, Name } = attachmentEntity?.File;
            FileStorage.get(Id).then((file) => {
                saveAs(file, Name);
            });
        } else {
            this.props.storage.selectFile(attachmentEntity.File);
        }
    };

    getFileTokens = (): IFileTokenProps[] => {
        return this.files.map(({ File }: IEntityAttachmentEntity) => ({
            id: File.Id?.toString(),
            name: File.Name,
            onClick: this.handleClick,
        }));
    };

    render() {
        return <FileInput
            passRef={this.fileInputRef}
            onNewFiles={this.handleNewFiles}
            isSharpLeft={this.props.isSharpLeft}
            isSharpRight={this.props.isSharpRight}
            onRemove={this.handleRemove}
            error={this.props.error}
            onClick={this.props.disableFileOpening ? null : this.handleClick}
            files={this.getFileTokens()}
            multiple={this.props.multiple}
            isLocalDropArea={this.props.useFullPageDropArea !== true}
            width={this.props.width}
            isDisabled={this.props.isDisabled}
            isReadOnly={this.props.isReadOnly}
            name={this.props.name}
        />;
    }
}

export default SmartFileInput;
